/**
 * The week as seven day cards - read-only.
 *
 * This is the Slice 1-3 body, kept verbatim rather than deleted, because
 * v4.23.0 ships the weekly coverage grid switched OFF for everybody except
 * admin (`scheduling_grid:view`). Anybody without that capability keeps
 * exactly the screen they have had since Slice 1: the week as generated, times
 * and posts, with nothing editable.
 *
 * It is not a placeholder and not an error state. A supervisor who opens
 * Scheduling on the day v4.23.0 goes out should see no change at all.
 */
import React, { useMemo } from 'react';
import { Moon, UserX } from 'lucide-react';

const DAY_LABELS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export function WeekCards({ dates, posts, shifts }) {
  const byDate = useMemo(() => {
    const m = {};
    (shifts || []).forEach((s) => {
      (m[s.shift_date] = m[s.shift_date] || []).push(s);
    });
    Object.values(m).forEach((list) => list.sort(
      (a, b) => String(a.time_in).localeCompare(String(b.time_in))));
    return m;
  }, [shifts]);
  const postName = (id) => (posts || []).find((p) => p.id === id)?.post_name || '—';

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-3"
      data-testid="scheduling-week-cards">
      {(dates || []).map((d, i) => (
        <div key={d} className="rounded-lg border border-line bg-surface-sunken p-3"
          data-testid={`scheduling-day-${d}`}>
          <div className="flex items-baseline justify-between">
            <span className="text-sm font-medium text-fg">{DAY_LABELS[i]}</span>
            <span className="text-xs text-fg-muted tabular-nums">{d.slice(5)}</span>
          </div>
          <div className="mt-2.5 space-y-2">
            {!(byDate[d] || []).length ? (
              <p className="text-xs text-fg-muted/70">—</p>
            ) : (byDate[d] || []).map((s) => (
              <div key={s.id} className="rounded-md border border-line bg-surface p-2"
                data-testid={`scheduling-shift-${s.id}`}>
                <p className="text-xs font-medium text-fg tabular-nums flex items-center gap-1">
                  {s.time_in}–{s.time_out}
                  {s.next_day && <Moon className="h-3 w-3 text-fg-muted" />}
                </p>
                <p className="text-xs text-fg-muted mt-0.5 truncate" title={postName(s.job_post_id)}>
                  {postName(s.job_post_id)}
                </p>
                {(s.is_open_post || !s.employee_id) && (
                  <p className="text-xs text-fg-muted mt-1 flex items-center gap-1">
                    <UserX className="h-3 w-3" /> Unfilled
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
